import { Loading } from "@/App";
import {
  clientTypeOverride,
  useExternalCRMOverride,
  useWasteSuctionSystemOverride,
  wtcsOverride,
} from "@/Configs/config";
import { useCommonEntitiesStore } from "@/States/commonEntities";
import { OptionalClientType, useConfig } from "@/api/hooks/useConfig";
import { StyledSelect } from "@/components/StyledSelect";
import { Toggle } from "@/components/Toggle";
import { Button } from "@/components/button/index";
import { Translate, useTrans } from "@/translations";

import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { SingleValue } from "react-select";

import { capitalize, isEqual, sortBy } from "lodash";
import { LanguageType } from "server/config-api/index.js";
import ClientType from "server/utils/clientType.js";
import timezones from "timezones-list";

import {
  MaxWeightPerThrowInput,
  getIsValidMaxWeight,
} from "./components/maxWeightPerThrowInput";
import { WasteCategoriesListForm } from "./components/wasteCategoriesListForm";
import { currencies } from "./payments";

type Props = {
  hasUnsavedChanges: boolean;
  setHasUnsavedChanges: (hasChanges: boolean) => void;
};

export const Client: FC<Props> = ({
  hasUnsavedChanges,
  setHasUnsavedChanges,
}) => {
  const { config, updateConfig, isUpdatingConfig, isMWM } = useConfig();
  const { wasteTypeClassificationSystems } = useCommonEntitiesStore();
  const { client } = useParams();

  const [clientType, setClientType] = useState<OptionalClientType>(
    config?.type,
  );
  const [useExternalCRM, setUseExternalCRM] = useState(
    config?.useExternalCRM ?? false,
  );
  const [useWasteSuctionSystem, setUseWasteSuctionSystem] = useState(
    config?.useWasteSuctionSystem ?? false,
  );
  const [wasteTypeClassificationSystem, setWasteTypeClassificationSystem] =
    useState<string>(config?.wasteTypeClassificationSystemId || "");
  const [timezone, setTimezone] = useState<string>(config?.timezone || "");
  const [language, setLanguage] = useState<LanguageType | undefined>(
    config?.language,
  );
  const [maxWeightPerThrow, setMaxWeightPerThrow] = useState<
    number | null | undefined
  >(config?.maxWeightPerThrow);
  const [currency, setCurrency] = useState<string | undefined>(
    config?.currency || undefined,
  );
  const [expectedWasteTypes, setExpectedWasteTypes] = useState<string[]>(
    config?.expectedWasteTypes || [],
  );
  const [enableWebWasteRegistration, setEnableWebWasteRegistration] = useState(
    config?.enableWebWasteRegistration,
  );
  const [enableTenantBuildingStatistics, setEnableTenantBuildingStatistics] =
    useState(config?.enableTenantBuildingStatistics || false);

  const { t } = useTrans();

  useEffect(() => {
    setUseExternalCRM(config?.useExternalCRM ?? false);
    setClientType(config?.type);
    setEnableWebWasteRegistration(config?.enableWebWasteRegistration);
  }, [config]);

  useEffect(() => {
    const hasChangedClientType = config?.type !== clientType;
    const hasChangedUseExternalCRM = config?.useExternalCRM !== useExternalCRM;
    const hasChangedWasteTypeClassificationSystem =
      !!wasteTypeClassificationSystem &&
      config?.wasteTypeClassificationSystemId !== wasteTypeClassificationSystem;
    const hasChangedExpectedWasteTypes = !isEqual(
      sortBy(config.expectedWasteTypes),
      sortBy(expectedWasteTypes),
    );
    const hasChangedLanguage = config?.language !== language;
    const hasChangedTimezone = config?.timezone !== timezone;
    const hasChangedMaxWeightPerThrow =
      config?.maxWeightPerThrow !== maxWeightPerThrow;
    const hasChangedEnableWebWasteRegistration =
      config?.enableWebWasteRegistration !== enableWebWasteRegistration;
    const hasChangedCurrency = config?.currency !== currency;
    const hasChangedEnableTenantBuildingStatistics =
      !!config?.enableTenantBuildingStatistics !=
      enableTenantBuildingStatistics;

    const hasChanges =
      hasChangedClientType ||
      hasChangedUseExternalCRM ||
      hasChangedWasteTypeClassificationSystem ||
      hasChangedExpectedWasteTypes ||
      hasChangedLanguage ||
      hasChangedTimezone ||
      hasChangedMaxWeightPerThrow ||
      hasChangedEnableWebWasteRegistration ||
      hasChangedCurrency ||
      hasChangedEnableTenantBuildingStatistics;
    setHasUnsavedChanges(hasChanges);
  }, [
    useExternalCRM,
    clientType,
    setHasUnsavedChanges,
    config,
    wasteTypeClassificationSystem,
    enableWebWasteRegistration,
    expectedWasteTypes,
    language,
    timezone,
    maxWeightPerThrow,
    currency,
    enableTenantBuildingStatistics,
  ]);

  const typeOptions = useMemo(() => {
    return Object.values(ClientType).map((type) => ({
      label: t(`common:${type}`),
      value: type,
    }));
  }, [t]);

  const wasteTypeClassificationSystemOptions = useMemo(() => {
    return (
      wasteTypeClassificationSystems?.map((w) => ({
        label: w.name,
        value: w.id,
      })) || []
    );
  }, [wasteTypeClassificationSystems]);

  const timezoneOptions = useMemo(
    () =>
      timezones
        .map((t) => ({
          label: t.label,
          value: t.tzCode,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [],
  );

  const languageOptions = useMemo(
    () =>
      Object.values(LanguageType)
        .map((code) => ({
          label: t(`languages:${code}`),
          value: code,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [t],
  );

  const onSave = useCallback(() => {
    if (isUpdatingConfig) return; // Prevent multiple submissions if already updating

    updateConfig({
      useExternalCRM,
      type: clientType!,
      wasteTypeClassificationSystemId: wasteTypeClassificationSystem,
      useWasteSuctionSystem,
      timezone,
      language,
      expectedWasteTypes,
      maxWeightPerThrow: maxWeightPerThrow ?? null,
      enableWebWasteRegistration,
      currency,
      enableTenantBuildingStatistics,
    });
  }, [
    isUpdatingConfig,
    updateConfig,
    useExternalCRM,
    clientType,
    wasteTypeClassificationSystem,
    useWasteSuctionSystem,
    timezone,
    language,
    expectedWasteTypes,
    maxWeightPerThrow,
    enableWebWasteRegistration,
    currency,
    enableTenantBuildingStatistics,
  ]);

  const onClientTypeChange = useCallback(
    (change: SingleValue<(typeof typeOptions)[number]>) => {
      setClientType(change?.value as ClientType);
    },
    [setClientType],
  );

  const onWasteTypeClassificationSystemChange = useCallback(
    (
      change: SingleValue<
        (typeof wasteTypeClassificationSystemOptions)[number]
      >,
    ) => {
      setWasteTypeClassificationSystem(change?.value as string);
    },
    [setWasteTypeClassificationSystem],
  );

  const onTimezoneChange = useCallback(
    (change: SingleValue<(typeof timezoneOptions)[number]>) => {
      setTimezone(change?.value as string);
    },
    [setTimezone],
  );

  const onLanguageChange = useCallback(
    (change: SingleValue<(typeof languageOptions)[number]>) => {
      setLanguage(change?.value as LanguageType);
    },
    [setLanguage],
  );

  const clientName = capitalize(client);
  const isValidMaxWeight = getIsValidMaxWeight(maxWeightPerThrow);

  if (!config) {
    return <Loading />;
  }

  return (
    <div>
      <div className="space-y-4">
        <h2 className="mb-4 text-2xl">{clientName}</h2>
        <div className="max-w-[380px]">
          <label className="select-none">
            {t("configLabels:customerSegment")} *
          </label>
          <StyledSelect
            className="mt-1 mb-4"
            required
            options={typeOptions}
            isDisabled={[
              ClientType.Mwm.toString(),
              ClientType.RealEstate.toString(),
            ].includes(clientTypeOverride ?? "")}
            value={typeOptions.find((o) => o.value === clientType)}
            onChange={onClientTypeChange}
          />
          <label className="select-none">
            {t("formLabels:defaultWTCSClient")} *
          </label>
          <StyledSelect
            className="mt-1 mb-4"
            options={wasteTypeClassificationSystemOptions}
            value={wasteTypeClassificationSystemOptions.find(
              (o) => o.value === wasteTypeClassificationSystem,
            )}
            isDisabled={wtcsOverride !== undefined}
            onChange={onWasteTypeClassificationSystemChange}
          />
          <label className="select-none">
            {t("formLabels:defaultTimezoneClient")} *
          </label>
          <StyledSelect
            className="mt-1 mb-4"
            options={timezoneOptions}
            value={timezoneOptions.find((o) => o.value === timezone)}
            onChange={onTimezoneChange}
          />
          <label className="select-none">
            {t("configLabels:clientLanguage")} *
          </label>
          <StyledSelect
            className="mt-1 mb-4"
            options={languageOptions}
            value={languageOptions?.find((o) => o.value === language)}
            onChange={onLanguageChange}
          />
          <MaxWeightPerThrowInput
            value={maxWeightPerThrow}
            onChange={setMaxWeightPerThrow}
            error={!isValidMaxWeight}
            label={t("configLabels:clientMaxWeight")}
            placeholder={t("formLabels:clientMaxWeightPlaceholder")}
          />
          <label className="select-none">{t("configLabels:currency")}</label>
          <StyledSelect
            className="mt-1 mb-4"
            options={currencies}
            value={currencies.find((c) => c.value === currency)}
            onChange={(v) => setCurrency(v.value)}
          />

          <div className="mt-4">
            <Toggle
              label={t("configLabels:enableTenantBuildingStatistics")}
              checked={enableTenantBuildingStatistics}
              onChange={() =>
                setEnableTenantBuildingStatistics(
                  !enableTenantBuildingStatistics,
                )
              }
            />
          </div>
        </div>
        {isMWM && (
          <div className="flex flex-col gap-y-4">
            <Toggle
              label={t("configLabels:useExternalCRM")}
              checked={useExternalCRM}
              disabled={useExternalCRMOverride !== undefined}
              onChange={() => setUseExternalCRM(!useExternalCRM)}
            />
            <Toggle
              label={t("configLabels:useWasteSuctionSystem")}
              checked={useWasteSuctionSystem}
              disabled={useWasteSuctionSystemOverride !== undefined}
              onChange={() => setUseWasteSuctionSystem(!useWasteSuctionSystem)}
            />
          </div>
        )}
      </div>
      {!isMWM && (
        <div className="mt-12 mb-4 space-y-4">
          <div className="mt-4 max-w-4xl text-sm">
            <div className="mb-2 font-medium">
              {t("configLabels:defaultWasteCategoriesTitle")}
            </div>
            <ul className="ml-2 list-inside list-disc">
              <Translate i18nKey="hints:expectedWasteCategories">
                <li />
              </Translate>
            </ul>
          </div>
          <WasteCategoriesListForm
            wasteCategoriesInConfig={expectedWasteTypes}
            onAddWasteCategory={setExpectedWasteTypes}
            onRemoveWasteCategory={setExpectedWasteTypes}
            isLoadingData={false}
            disabled={isUpdatingConfig}
            className="max-w-4xl"
            onConfirmAddText="actions:addNew"
          />
        </div>
      )}
      <hr className="-mx-4" />
      <Button
        label="actions:save"
        loading={isUpdatingConfig}
        onClick={onSave}
        disabled={isUpdatingConfig || !hasUnsavedChanges || !isValidMaxWeight}
        className="mt-4"
      />
    </div>
  );
};
